import { t } from 'i18next';
import { path, equals } from 'ramda';
import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  fetchCompanyFieldsScores,
  updateCompanyFieldsScores,
} from '../../../../modules/companyFieldsScores/companyFieldsScoresActions';
import { useForm } from '../../../../utils/hooks/useForm';
import { DisplayFlexS } from '../../../../utils/stylesHelpers';
import {
  ALL_FIELDS,
  MATCHING_CRITERIA_SCORES_FIELDS,
  PREMIUM_TIERS_SCORES_FIELDS,
  PROFILE_COMPLETENESS_FIELDS,
} from '../../../constants/companyFieldsScores';
import { TextInput } from '../../common';
import SaveBtn from '../../companyEdit/common/saveBtn';

import {
  SaveButtonWrapper,
  InputWrapper,
  ScoresSumTitle,
  ScoreSectionTitle,
  FormSection,
  AdjustmentScoresTitle,
} from './companyFieldsScoresS';
import { buildValidationSchema } from './helpers';

const validationSchema = buildValidationSchema(ALL_FIELDS);

const renderFields = ({ fields, values, errors, touched, onChange, onBlur }) =>
  fields.map((field) => (
    <InputWrapper key={field.name}>
      <TextInput
        label={field.label}
        name={field.name}
        type="number"
        step="any"
        value={values[field.name]}
        onChange={onChange}
        onBlur={onBlur}
        isError={path([field.name], errors) && path([field.name], touched)}
        error={path([field.name], errors)}
      />
    </InputWrapper>
  ));

const CompanyFieldsScoresComponent = () => {
  const dispatch = useDispatch();
  const { scores = {}, loading } = useSelector((state) => state.companyFieldsScores);

  useEffect(() => {
    dispatch(fetchCompanyFieldsScores());
  }, []);

  const initialValues = useMemo(() => scores, [scores]);
  const hasChanges = (values) => !equals(scores, values);

  const { values, errors, touched, onChange, onBlur, onSubmit, dirty, resetForm } = useForm({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      dispatch(updateCompanyFieldsScores(values));
      resetForm();
    },
  });

  const profileCompletenessSum = useMemo(
    () => PROFILE_COMPLETENESS_FIELDS.reduce((acc, field) => acc + (parseFloat(values[field.name]) || 0), 0),
    [values]
  );

  return (
    <DisplayFlexS direction="column" directionScreenMedium="column">
      <AdjustmentScoresTitle>{t('adminPanel.dashbord.adjustmentScoresTitle')}</AdjustmentScoresTitle>
      {!loading && (
        <form onSubmit={onSubmit}>
          <FormSection>
            <div>
              <ScoreSectionTitle>{t('adminPanel.dashbord.profileCompletenessScoresTitle')}</ScoreSectionTitle>
              {renderFields({
                fields: PROFILE_COMPLETENESS_FIELDS,
                values,
                errors,
                touched,
                onChange,
                onBlur,
              })}
              <ScoresSumTitle>
                {t('adminPanel.dashbord.sumOfScoresTitle')}: {profileCompletenessSum}
              </ScoresSumTitle>
            </div>
            <div>
              <ScoreSectionTitle variant="h4">{t('adminPanel.dashbord.premiumTiersScoresTitle')}</ScoreSectionTitle>
              {renderFields({
                fields: PREMIUM_TIERS_SCORES_FIELDS,
                values,
                errors,
                touched,
                onChange,
                onBlur,
              })}
              <ScoreSectionTitle>{t('adminPanel.dashbord.matchingCriteriaScoresTitle')}</ScoreSectionTitle>
              {renderFields({
                fields: MATCHING_CRITERIA_SCORES_FIELDS,
                values,
                errors,
                touched,
                onChange,
                onBlur,
              })}
            </div>
          </FormSection>
          <SaveButtonWrapper marginTop="30" marginTopMobile="90">
            <SaveBtn loading={loading} disabled={!dirty && !hasChanges(values)} />
          </SaveButtonWrapper>
        </form>
      )}
    </DisplayFlexS>
  );
};

export default CompanyFieldsScoresComponent;
